export type ImageStyleCategory =
  | "no_style"
  | "photorealistic"
  | "cinematic"
  | "editorial"
  | "fashion_photography"
  | "film_photography"
  | "polaroid"
  | "black_and_white_photography"
  | "vintage_photography"
  | "oil_painting"
  | "acrylic_painting"
  | "watercolor"
  | "gouache"
  | "pastel"
  | "ink_wash"
  | "abstract_painting"
  | "pencil_drawing"
  | "colored_pencil"
  | "charcoal_sketch"
  | "ink_drawing"
  | "pen_and_ink"
  | "marker_drawing"
  | "chalk_drawing"
  | "illustration"
  | "digital_art"
  | "vector_art"
  | "flat_illustration"
  | "graphic_poster"
  | "comic_book"
  | "graphic_novel"
  | "pop_art"
  | "minimal"
  | "collage"
  | "mixed_media"
  | "screen_print"
  | "risograph"
  | "linocut"
  | "woodcut"
  | "paper_cut"
  | "art_deco"
  | "art_nouveau"
  | "bauhaus"
  | "cubism"
  | "surrealism"
  | "impressionism"
  | "expressionism"
  | "retro"
  | "y2k"
  | "psychedelic"
  | "vaporwave"
  | "synthwave"
  | "grunge"
  | "futuristic"
  | "cyberpunk"
  | "three_d_render"
  | "clay_sculptural"
  | "low_poly"
  | "pixel_art"
  | "isometric"
  | "graffiti_spray_paint"
  | "airbrush"
  | "tattoo_art"
  | "mosaic"
  | "stained_glass";

type StyleGroupLabel =
  | "Default"
  | "Photography & Realism"
  | "Painting"
  | "Drawing & Sketch"
  | "Illustration & Graphic"
  | "Print & Mixed Media"
  | "Classic Art Movements"
  | "Retro & Contemporary Aesthetics"
  | "3D & Digital"
  | "Decorative & Experimental";

type StyleDefinition = {
  label: string;
  stylePhrase: string;
  group: StyleGroupLabel;
};

export type StyleCategoryOption = {
  value: ImageStyleCategory;
  label: string;
};

export type StyleCategoryGroup = {
  groupLabel: StyleGroupLabel;
  options: StyleCategoryOption[];
};

export const STYLE_CATEGORIES: Record<ImageStyleCategory, StyleDefinition> = {
  no_style: {
    label: "No style",
    stylePhrase: "",
    group: "Default",
  },
  photorealistic: {
    label: "Photorealistic",
    stylePhrase: "photorealistic, lifelike detail, natural lighting",
    group: "Photography & Realism",
  },
  cinematic: {
    label: "Cinematic",
    stylePhrase: "cinematic framing, dramatic lighting, filmic color grade",
    group: "Photography & Realism",
  },
  editorial: {
    label: "Editorial",
    stylePhrase: "editorial photography, magazine composition, refined styling",
    group: "Photography & Realism",
  },
  fashion_photography: {
    label: "Fashion Photography",
    stylePhrase:
      "fashion photography, high-end styling, confident studio presence",
    group: "Photography & Realism",
  },
  film_photography: {
    label: "Film Photography",
    stylePhrase: "35mm film photography, analog grain, soft halation",
    group: "Photography & Realism",
  },
  polaroid: {
    label: "Polaroid",
    stylePhrase:
      "polaroid instant photo, washed tones, soft vignette, square frame",
    group: "Photography & Realism",
  },
  black_and_white_photography: {
    label: "Black & White Photography",
    stylePhrase: "black and white photography, rich tonal range, high contrast",
    group: "Photography & Realism",
  },
  vintage_photography: {
    label: "Vintage Photography",
    stylePhrase: "vintage photography, faded palette, aged print texture",
    group: "Photography & Realism",
  },
  oil_painting: {
    label: "Oil Painting",
    stylePhrase: "oil painting, visible brushstrokes, layered impasto texture",
    group: "Painting",
  },
  acrylic_painting: {
    label: "Acrylic Painting",
    stylePhrase: "acrylic painting, bold opaque color, crisp edges",
    group: "Painting",
  },
  watercolor: {
    label: "Watercolor",
    stylePhrase: "watercolor painting, translucent washes, bleeding pigment",
    group: "Painting",
  },
  gouache: {
    label: "Gouache",
    stylePhrase: "gouache painting, matte opaque color, flat painterly shapes",
    group: "Painting",
  },
  pastel: {
    label: "Pastel",
    stylePhrase: "soft pastel painting, powdery texture, blended color",
    group: "Painting",
  },
  ink_wash: {
    label: "Ink Wash",
    stylePhrase: "ink wash painting, fluid gradients, sparse brushwork",
    group: "Painting",
  },
  abstract_painting: {
    label: "Abstract Painting",
    stylePhrase:
      "abstract painting, expressive gestural forms, balanced contrast",
    group: "Painting",
  },
  pencil_drawing: {
    label: "Pencil Drawing",
    stylePhrase: "graphite pencil drawing, fine hatching, soft shading",
    group: "Drawing & Sketch",
  },
  colored_pencil: {
    label: "Colored Pencil",
    stylePhrase:
      "colored pencil drawing, layered strokes, subtle paper texture",
    group: "Drawing & Sketch",
  },
  charcoal_sketch: {
    label: "Charcoal Sketch",
    stylePhrase: "charcoal sketch, smudged shadows, dramatic tonal contrast",
    group: "Drawing & Sketch",
  },
  ink_drawing: {
    label: "Ink Drawing",
    stylePhrase: "ink drawing, confident linework, high contrast blacks",
    group: "Drawing & Sketch",
  },
  pen_and_ink: {
    label: "Pen & Ink",
    stylePhrase: "pen and ink illustration, cross-hatching, precise detail",
    group: "Drawing & Sketch",
  },
  marker_drawing: {
    label: "Marker Drawing",
    stylePhrase: "marker drawing, bold saturated strokes, visible blending",
    group: "Drawing & Sketch",
  },
  chalk_drawing: {
    label: "Chalk Drawing",
    stylePhrase: "chalk drawing, grainy texture, soft dusty color",
    group: "Drawing & Sketch",
  },
  illustration: {
    label: "Illustration",
    stylePhrase: "illustration, stylized shapes, clean rendering",
    group: "Illustration & Graphic",
  },
  digital_art: {
    label: "Digital Art",
    stylePhrase: "digital art, polished rendering, vivid color",
    group: "Illustration & Graphic",
  },
  vector_art: {
    label: "Vector Art",
    stylePhrase: "vector art, crisp geometry, flat color fills",
    group: "Illustration & Graphic",
  },
  flat_illustration: {
    label: "Flat Illustration",
    stylePhrase:
      "flat illustration, simple shapes, limited palette, no shading",
    group: "Illustration & Graphic",
  },
  graphic_poster: {
    label: "Graphic Poster",
    stylePhrase:
      "graphic poster design, bold composition, strong figure-ground contrast",
    group: "Illustration & Graphic",
  },
  comic_book: {
    label: "Comic Book",
    stylePhrase: "comic book art, inked outlines, halftone shading",
    group: "Illustration & Graphic",
  },
  graphic_novel: {
    label: "Graphic Novel",
    stylePhrase:
      "graphic novel art, moody panel composition, restrained palette",
    group: "Illustration & Graphic",
  },
  pop_art: {
    label: "Pop Art",
    stylePhrase: "pop art, bold primaries, ben-day dots, graphic outlines",
    group: "Illustration & Graphic",
  },
  minimal: {
    label: "Minimal",
    stylePhrase: "minimal composition, negative space, restrained palette",
    group: "Illustration & Graphic",
  },
  collage: {
    label: "Collage",
    stylePhrase: "cut-paper collage, torn edges, layered fragments",
    group: "Print & Mixed Media",
  },
  mixed_media: {
    label: "Mixed Media",
    stylePhrase:
      "mixed media artwork, combined paint and print textures, layered surfaces",
    group: "Print & Mixed Media",
  },
  screen_print: {
    label: "Screen Print",
    stylePhrase: "screen print, flat spot colors, slight registration offset",
    group: "Print & Mixed Media",
  },
  risograph: {
    label: "Risograph",
    stylePhrase:
      "risograph print, duotone inks, grainy overprint, misregistration",
    group: "Print & Mixed Media",
  },
  linocut: {
    label: "Linocut",
    stylePhrase: "linocut print, carved marks, bold black and white shapes",
    group: "Print & Mixed Media",
  },
  woodcut: {
    label: "Woodcut",
    stylePhrase: "woodcut print, rough grain, stark relief carving",
    group: "Print & Mixed Media",
  },
  paper_cut: {
    label: "Paper Cut",
    stylePhrase: "paper cut art, layered silhouettes, soft drop shadows",
    group: "Print & Mixed Media",
  },
  art_deco: {
    label: "Art Deco",
    stylePhrase:
      "art deco, symmetrical geometry, gold linework, elegant ornament",
    group: "Classic Art Movements",
  },
  art_nouveau: {
    label: "Art Nouveau",
    stylePhrase: "art nouveau, flowing organic linework, botanical ornament",
    group: "Classic Art Movements",
  },
  bauhaus: {
    label: "Bauhaus",
    stylePhrase:
      "bauhaus design, primary colors, geometric forms, functional composition",
    group: "Classic Art Movements",
  },
  cubism: {
    label: "Cubism",
    stylePhrase: "cubism, fragmented planes, multiple viewpoints",
    group: "Classic Art Movements",
  },
  surrealism: {
    label: "Surrealism",
    stylePhrase: "surrealism, dreamlike juxtaposition, impossible forms",
    group: "Classic Art Movements",
  },
  impressionism: {
    label: "Impressionism",
    stylePhrase: "impressionism, broken brushwork, luminous natural light",
    group: "Classic Art Movements",
  },
  expressionism: {
    label: "Expressionism",
    stylePhrase: "expressionism, distorted forms, emotive color, raw energy",
    group: "Classic Art Movements",
  },
  retro: {
    label: "Retro",
    stylePhrase: "retro aesthetic, vintage palette, aged print texture",
    group: "Retro & Contemporary Aesthetics",
  },
  y2k: {
    label: "Y2K",
    stylePhrase:
      "y2k aesthetic, metallic sheen, glossy chrome, early web gloss",
    group: "Retro & Contemporary Aesthetics",
  },
  psychedelic: {
    label: "Psychedelic",
    stylePhrase:
      "psychedelic art, swirling patterns, vivid clashing color, warped forms",
    group: "Retro & Contemporary Aesthetics",
  },
  vaporwave: {
    label: "Vaporwave",
    stylePhrase:
      "vaporwave aesthetic, pastel neon, retro computer graphics, hazy nostalgia",
    group: "Retro & Contemporary Aesthetics",
  },
  synthwave: {
    label: "Synthwave",
    stylePhrase:
      "synthwave aesthetic, neon grid horizon, magenta and cyan glow",
    group: "Retro & Contemporary Aesthetics",
  },
  grunge: {
    label: "Grunge",
    stylePhrase: "grunge aesthetic, distressed texture, gritty muted palette",
    group: "Retro & Contemporary Aesthetics",
  },
  futuristic: {
    label: "Futuristic",
    stylePhrase: "futuristic design, sleek surfaces, synthetic materials",
    group: "Retro & Contemporary Aesthetics",
  },
  cyberpunk: {
    label: "Cyberpunk",
    stylePhrase:
      "cyberpunk aesthetic, neon-lit night, dense urban tech, high contrast",
    group: "Retro & Contemporary Aesthetics",
  },
  three_d_render: {
    label: "3D Render",
    stylePhrase: "3d render, physically based shading, studio lighting",
    group: "3D & Digital",
  },
  clay_sculptural: {
    label: "Clay / Sculptural",
    stylePhrase: "clay render, sculptural forms, matte surfaces, soft shadows",
    group: "3D & Digital",
  },
  low_poly: {
    label: "Low Poly",
    stylePhrase: "low poly 3d, faceted geometry, flat shaded planes",
    group: "3D & Digital",
  },
  pixel_art: {
    label: "Pixel Art",
    stylePhrase: "pixel art, limited palette, crisp pixel grid, dithering",
    group: "3D & Digital",
  },
  isometric: {
    label: "Isometric",
    stylePhrase: "isometric projection, clean parallel geometry, even lighting",
    group: "3D & Digital",
  },
  graffiti_spray_paint: {
    label: "Graffiti / Spray Paint",
    stylePhrase:
      "graffiti spray paint, aerosol texture, bold tags, wall surface",
    group: "Decorative & Experimental",
  },
  airbrush: {
    label: "Airbrush",
    stylePhrase: "airbrush art, smooth gradients, glossy highlights",
    group: "Decorative & Experimental",
  },
  tattoo_art: {
    label: "Tattoo Art",
    stylePhrase: "tattoo art, bold outlines, limited ink palette, flash sheet",
    group: "Decorative & Experimental",
  },
  mosaic: {
    label: "Mosaic",
    stylePhrase: "mosaic, tessellated tiles, grouted seams, textured color",
    group: "Decorative & Experimental",
  },
  stained_glass: {
    label: "Stained Glass",
    stylePhrase:
      "stained glass, leaded outlines, luminous translucent color panes",
    group: "Decorative & Experimental",
  },
};

export const DEFAULT_STYLE_CATEGORY: ImageStyleCategory = "no_style";

const STYLE_GROUP_ORDER: StyleGroupLabel[] = [
  "Default",
  "Photography & Realism",
  "Painting",
  "Drawing & Sketch",
  "Illustration & Graphic",
  "Print & Mixed Media",
  "Classic Art Movements",
  "Retro & Contemporary Aesthetics",
  "3D & Digital",
  "Decorative & Experimental",
];

export const STYLE_CATEGORY_OPTIONS = (
  Object.entries(STYLE_CATEGORIES) as Array<
    [ImageStyleCategory, StyleDefinition]
  >
).map(([value, definition]) => ({
  value,
  label: definition.label,
}));

export const STYLE_CATEGORY_GROUPS: StyleCategoryGroup[] =
  STYLE_GROUP_ORDER.map((groupLabel) => ({
    groupLabel,
    options: STYLE_CATEGORY_OPTIONS.filter(
      (option) => STYLE_CATEGORIES[option.value].group === groupLabel,
    ),
  })).filter((group) => group.options.length > 0);

export const RANDOM_PROMPTS = [
  "abstract shapes and textures, moody lighting",
  "minimalist composition with soft gradients",
  "surreal atmosphere, dreamlike and calm",
  "organic forms, natural flow, subtle contrast",
  "cinematic lighting with an ambiguous subject",
  "modern abstract art, balanced and simple",
  "visual interpretation of sound and rhythm",
  "abstract representation of a musical mood",
  "imagined cover art inspired by emotion in music",
  "atmospheric image influenced by tempo and flow",
  "conceptual artwork suggesting harmony and tension",
  "visual form inspired by layers and movement in sound",
];

export const VARIATIONS = [
  "with subtle grain",
  "soft contrast",
  "ambient lighting",
  "muted color palette",
  "high dynamic range",
  "slow tempo feeling",
  "dynamic rhythm energy",
  "sense of buildup and release",
  "calm, sustained tone",
  "fragmented and syncopated",
];

const IMAGE_ONLY_GUARDRAIL_PROMPT =
  "no text, no typography, no letters, no words, no logo, no watermark, no signature, no branding";

function getRandomEntry<T>(values: readonly T[]): T {
  return values[Math.floor(Math.random() * values.length)];
}

function normalizePromptText(value: string): string {
  return value
    .trim()
    .replace(/\s+/g, " ")
    .replace(/[.,;:!?]+$/g, "");
}

function joinPromptParts(parts: Array<string | null | undefined>): string {
  return parts
    .map((part) => normalizePromptText(part ?? ""))
    .filter(Boolean)
    .join(", ");
}

export function getStyleLabel(styleCategory: ImageStyleCategory): string {
  return STYLE_CATEGORIES[styleCategory].label;
}

export function composeStyledPrompt({
  userPrompt,
  styleCategory,
}: {
  userPrompt: string;
  styleCategory: ImageStyleCategory;
}): string {
  const cleanedUserPrompt = normalizePromptText(userPrompt);
  const stylePhrase = STYLE_CATEGORIES[styleCategory].stylePhrase;

  return joinPromptParts([
    cleanedUserPrompt,
    stylePhrase,
    IMAGE_ONLY_GUARDRAIL_PROMPT,
  ]);
}

export function buildRandomStyledDraftPrompt(
  styleCategory: ImageStyleCategory,
): string {
  return getRandomPromptWithVariation(styleCategory);
}

export function getRandomPromptWithVariation(
  styleCategory?: ImageStyleCategory,
): string {
  const base = getRandomEntry(RANDOM_PROMPTS);
  const variation = getRandomEntry(VARIATIONS);
  const selectedStyle = styleCategory ?? DEFAULT_STYLE_CATEGORY;
  const stylePhrase = STYLE_CATEGORIES[selectedStyle].stylePhrase;

  return joinPromptParts([
    base,
    variation,
    stylePhrase,
    IMAGE_ONLY_GUARDRAIL_PROMPT,
  ]);
}
